import React from 'react';
import {
    Box, CardContent,
    CardHeader,
    Divider, List, ListItemText,
} from '@material-ui/core';
import { isNumber, reduce, size } from 'lodash/fp';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import DateRangeIcon from '@material-ui/icons/DateRange';
import WatchLaterIcon from '@material-ui/icons/WatchLater';
import styled from 'styled-components';
import CancelIcon from '@material-ui/icons/Cancel';
import { useTranslation } from 'react-i18next';
import { usePriceService } from '../../../../utils/hooks/priceService';
import {
    BillQuantityBadge, BillsCount, BillsLabel, Card,
    ListItemDense, ListItemIconDense, SmallAvatar, TableIcon, Typography,
} from '../../../../ui-kit';
import { getFullName, getInitials } from '../../../../utils';
import { OrderState } from '../../../../server-types';
import { useDateTimeService } from '../../../../utils/hooks/dateTimeService';
import { useUser } from '../../../../utils/hooks/auth';
import { OrderItemProps } from './types';

const RelativeCard = styled(Card)`
    position: relative;
`;

const Badge = styled(BillQuantityBadge)`
    position: absolute;
    padding: 5px 0;
    ${({ theme }) => `
        top: ${theme.spacing(1)}px;
        right: ${theme.spacing(2)}px;
    `}
`;

const Order = ({ order }: OrderItemProps) => {
    const { t } = useTranslation();
    const { priceService } = usePriceService();
    const { dateTimeService } = useDateTimeService();
    const { user } = useUser();

    const {
        openedBy: { account: { imageUrl, firstName, lastName } },
        place,
        name,
        orderNumber,
        createdUtc,
        orderState,
        orderLines,
        bills,
    } = order;

    const date = dateTimeService.localizeDate(createdUtc, user.cultureCode);
    const time = dateTimeService.localizeTime(createdUtc, user.cultureCode);

    const fullName = getFullName({ firstName, lastName });
    const initials = getInitials({ firstName, lastName });
    const placeName = place ? place.name: t('Take away');

    const orderDisplayName = name || orderNumber || placeName;

    const orderName = (
        <Box display='flex' alignItems='center'>
            {orderState === OrderState.Canceled && <CancelIcon color='error' />}
            <Typography variant='h6'>
                {orderDisplayName}
            </Typography>
        </Box>
    );
    const pendingAmount = reduce((acc, { price }) => (acc + price), 0, orderLines);
    let hasDiscount = false;
    const amountPaid = reduce((acc, { total, dicountedTotalValue, roundedPrice }) => {
        if (!hasDiscount) {
            hasDiscount = isNumber(dicountedTotalValue);
        }

        // eslint-disable-next-line no-nested-ternary
        const totalPrice = isNumber(roundedPrice)
            ? roundedPrice
            : isNumber(dicountedTotalValue)
                ? dicountedTotalValue
                : total;
        return priceService.add(totalPrice, acc);
    },
    0,
    bills
    );

    // TODO: Orders should actually represent the hwole amount which was meant to pay, but it's
    // harder to fix in one short time
    const finalAmount = amountPaid === 0 && !hasDiscount ? pendingAmount : amountPaid;
    const formattedTotal = priceService.toLocaleString(finalAmount);
    const billsQuantity = size(bills);

    return (
        <RelativeCard>
            {billsQuantity > 0 && (
                <Badge>
                    <BillsCount>
                        {billsQuantity}
                    </BillsCount>
                    <BillsLabel>{t('Bill', { count: billsQuantity })}</BillsLabel>
                </Badge>
            )}
            <CardHeader disableTypography title={orderName} />
            <Divider variant='middle' />
            <CardContent>
                <List dense>
                    <ListItemDense>
                        <ListItemIconDense>
                            <SmallAvatar src={imageUrl || undefined}>
                                {initials}
                            </SmallAvatar>
                        </ListItemIconDense>
                        <ListItemText primary={fullName} />
                    </ListItemDense>
                    <ListItemDense>
                        <ListItemIconDense>
                            <WatchLaterIcon />
                        </ListItemIconDense>
                        <ListItemText primary={time} />
                        <ListItemIconDense>
                            <DateRangeIcon />
                        </ListItemIconDense>
                        <ListItemText primary={date} />
                    </ListItemDense>
                    <ListItemDense>
                        <ListItemIconDense>
                            <TableIcon />
                        </ListItemIconDense>
                        <ListItemText primary={placeName} />
                    </ListItemDense>
                    <ListItemDense>
                        <Box display='flex' flexGrow={1} justifyContent='space-between'>
                            <Box display='flex' justifySelf="flex-start">
                                <ListItemIconDense>
                                    <MonetizationOnIcon />
                                </ListItemIconDense>
                                <ListItemText primary={formattedTotal} />
                            </Box>
                            {/* <Box display='flex' justifySelf="flex-end">
                                <ListItemText primary={`Order is ${orderState.toLowerCase()}`} />
                                <ListItemIconDense reverse>
                                    {orderState === OrderState.Opened ?
                                        <MeetingRoomIcon /> :
                                        <NoMeetingRoomIcon />}
                                </ListItemIconDense>
                            </Box> */}
                        </Box>
                    </ListItemDense>
                </List>
            </CardContent>
        </RelativeCard>
    );
};

export default Order;
